import React from 'react';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import dayjs from 'dayjs';

const ChartContainer = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data || data.length === 0) {
    return (
      <Typography variant="body1" textAlign="center">
        No data to display. Please select pages and a date range.
      </Typography>
    );
  }

  // **Updated: Fix for Monthly Data ("YYYYMM")**
  const formatDateLabel = (value) => {
    const valueStr = String(value); // Ensure it's a string

    if (valueStr.length === 8) {
      // **Daily Format: YYYYMMDD -> "15 Sept 2024"**
      const date = dayjs(valueStr, 'YYYYMMDD');
      if (!date.isValid()) return valueStr;
      return `${date.date()} ${date.format('MMM')} ${date.year()}`;
    }

    if (valueStr.length === 6) {
      // **Monthly Format: YYYYMM -> "Sept 2024"**
      const date = dayjs(valueStr, 'YYYYMM');
      if (!date.isValid()) return valueStr;
      return `${date.format('MMM')} ${date.year()}`;
    }

    if (valueStr.length === 4) {
      // **Yearly Format: YYYY -> "2024"**
      return valueStr;
    }

    return valueStr; // Fallback
  };

  // Build the dataset
  const dataset = [];
  const uniqueDates = new Set();

  data.forEach((series) => {
    series.data.forEach((point) => uniqueDates.add(point.x));
  });

  const sortedDates = [...uniqueDates].sort();
  sortedDates.forEach((dateStr) => {
    let entry = { date: dateStr };
    data.forEach((series) => {
      const point = series.data.find((p) => p.x === dateStr);
      entry[series.id] = point ? point.y : null;
    });
    dataset.push(entry);
  });

  // Compute max Y value for domain
  const maxDataValue = Math.max(
    ...data.flatMap((series) => series.data.map((p) => p.y).filter((y) => y !== null))
  );

  const baseHeight = isMobile ? 300 : 400;
  const legendPadding = 90;
  const firstDate = data[0].date;
  const lastDate = data[data.length - 1].date;

  return (
    <div style={{ textAlign: 'center', width: '100%', margin: 'auto', padding: 10 }} className="custom-y-padding-bottom">
      <LineChart
        dataset={dataset}
        xAxis={[
          {
            dataKey: 'date',
            scaleType: 'band',
            label: 'Date',
            labelStyle: {
                fontSize: 13,
            },
            valueFormatter: (value) => formatDateLabel(value), // ✅ Now correctly formats all cases
            tickLabelStyle: {
              fontSize: isMobile ? '10px' : '12px',
              angle: isMobile ? -45 : 0,
              textAnchor: isMobile ? 'end' : 'middle',
              opacity: 0,
            },
          },
        ]}
        yAxis={[
          {
            label: isMobile ? '' : 'Page Views',
            labelStyle: {
                fontSize: 13,
            },
            tickLabelStyle: {
              fontSize: isMobile ? '10px' : '11px',
            },
            grid: { strokeDasharray: '3 3', stroke: theme.palette.grey[300] },
          },
        ]}
        series={data.map((s) => ({
          dataKey: s.id,
          label: s.label, // Legend uses the label field.
          link: s.link, // Clicking legend opens this link.
          connectNulls: true,
        }))}
        maxWidth={isMobile ? 350 : '100%'}
        minWidth={isMobile ? '100%' : 350}
        height={baseHeight + legendPadding}
        margin={{
          top: 40 + legendPadding,
          right: 20,
          bottom: isMobile ? 60 : 40,
          left: 50,
        }}
        legend={{
            position: { vertical: 'top', horizontal: 'center' },
            fontSize: theme.typography.datePicker,
            padding: 10,
            itemGap: 10,
            maxHeight: 110,
            labelStyle: {
                fontSize: 12,
            },
            sx: {
              "& .MuiChartsLegend-item": {
                cursor: "pointer",
                textDecoration: "none",
              },
            },
          }}
          
      />
    </div>
  );
};

export default ChartContainer;
